import React, { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const gbp = (v) => `${v < 0 ? "-" : ""}£${Math.abs(v || 0).toFixed(2)}`;

const Line = ({ label, value, minus, bold, testId }) => (
  <div className={`flex justify-between py-1.5 border-t border-[#eee9dc] ${bold ? "font-medium text-[#2b2b2b]" : "text-[#4b4b4b]"}`} data-testid={testId}>
    <span>{label}</span><span className="tabular-nums">{minus ? `− ${gbp(value)}` : gbp(value)}</span>
  </div>
);

const Stat = ({ label, value, testId }) => (
  <div className="rounded-xl border border-[#e5e0d3] bg-white p-4" data-testid={testId}>
    <p className="text-xs uppercase tracking-wider text-[#6b6b6b]">{label}</p>
    <p className="mt-1 text-2xl text-[#2b2b2b]">{value}</p>
  </div>
);

// Admin: unit economics + founder distribution for the chosen month.
export const EconomicsPanel = ({ ym }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    axios.get(`${API}/api/admin/costs/economics`, { params: { ym }, headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` } })
      .then((r) => setD(r.data)).catch(() => setD({ error: true }));
  }, [ym]);
  if (!d) return null;
  if (d.error) return <p className="mt-6 text-sm text-[#a0522d]" data-testid="economics-error">Couldn't load the money summary.</p>;
  return (
    <section className="mt-6 rounded-2xl border border-[#e5e0d3] bg-[#fbfaf6] p-5" data-testid="economics-panel">
      <h2 className="text-lg text-[#2b2b2b]" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>Money this month</h2>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Paid members" value={`${d.paid_members}`} testId="economics-paid-members" />
        <Stat label="Monthly / yearly" value={`${d.monthly_members} / ${d.yearly_members}`} testId="economics-plan-split" />
        <Stat label="Network Principals total" value={gbp(d.founder_total)} testId="economics-founder-total" />
        <Stat label="Per Principal (2)" value={gbp(d.founder_each)} testId="economics-founder-each" />
      </div>
      <div className="mt-4 max-w-xl text-sm">
        <Line label={`Paid in (${d.payments} payments)`} value={d.gross} testId="economics-gross" />
        <Line label="Refunds" value={d.refunds} minus testId="economics-refunds" />
        <Line label="VAT (Stripe pays it)" value={d.vat} minus testId="economics-vat" />
        <Line label="Net revenue" value={d.net} bold testId="economics-net" />
        <Line label="Stripe fees (estimate)" value={d.stripe_fees} minus testId="economics-stripe-fees" />
        <Line label="AI + voice costs" value={d.ai_cost} minus testId="economics-ai-cost" />
        <Line label="Affiliate commission (draft, engine pending)" value={d.partner_commission} minus testId="economics-commission" />
        <Line label="Operating contribution" value={d.contribution} bold testId="economics-contribution" />
        <Line label="Network Principals (60p per member)" value={d.founder_total} minus testId="economics-founder-line" />
        <Line label="Soulumo contribution" value={d.after_founders} bold testId="economics-after-founders" />
      </div>
      {d.partners.length > 0 && (
        <p className="mt-3 text-xs text-[#6b6b6b]" data-testid="economics-partners">
          Commission by partner: {d.partners.map((p) => `${p.via} ${gbp(p.commission)}`).join(" · ")}
        </p>
      )}
      <p className="mt-2 text-xs text-[#6b6b6b]" data-testid="economics-per-member">
        Per member: net {gbp(d.per_member.net)} · fees {gbp(d.per_member.stripe_fees)} · AI {gbp(d.per_member.ai_cost)} · contribution {gbp(d.per_member.contribution)}
      </p>
      <p className="mt-2 text-xs text-[#8a8a8a]">{d.assumptions}</p>
    </section>
  );
};
